import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClientCombobox } from '@/components/common/EntityCombobox';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { showErrorToast } from '@/lib/api';
import { useStore } from '@/lib/store';
import { Plus, Trash2, CheckCircle, XCircle, Paperclip } from 'lucide-react';
import type { AMLForm, AMLFormCreate, AMLFormUpdate, FormGroup, FormField, FieldType } from '@/types/aml-forms';
import { 
  createAMLForm, 
  updateAMLForm, 
  downloadDocumentAuthenticated,
} from '@/services/aml-forms';
import { DocumentUpload, DOCUMENT_TYPE_LABELS } from './DocumentUpload';

interface AMLFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: AMLForm | null;
  templateForm?: AMLForm | null;
  onSaved: () => void;
}

export function AMLFormDialog({ open, onOpenChange, form, templateForm, onSaved }: AMLFormDialogProps) {
  const { toast } = useToast();
  const { clients, fetchClients } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    client: 0,
    name: '',
    description: '',
    is_checked: false,
  });

  const [groups, setGroups] = useState<FormGroup[]>([
    {
      groupName: 'Company Questions',
      fields: [
        { key: 'company_name', label: 'Company Name', type: 'text' as FieldType, required: true, defaultValue: '' },
      ],
    },
  ]);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (form) {
      // Editing an existing form
      setFormData({
        client: form.client,
        name: form.name,
        description: form.description,
        is_checked: form.is_checked,
      });
      
      if (form.form_structure && form.form_structure.groups) {
        // If form is completed, merge actual filled values into the structure
        if (form.is_completed && form.form_data) {
          const updatedGroups: FormGroup[] = form.form_structure.groups.map((group) => ({
            ...group,
            fields: group.fields.map(field => {
              // For repeatable groups, we'll show the structure but values are in form_data
              // For non-repeatable, merge the actual value
              if (!group.repeatable) {
                const actualValue = form.form_data?.[field.key];
                return {
                  ...field,
                  defaultValue: actualValue !== undefined ? actualValue : field.defaultValue
                };
              }
              return field;
            })
          }));
          setGroups(updatedGroups);
        } else {
          setGroups(form.form_structure.groups);
        }
      } else if (form.form_data && Object.keys(form.form_data).length > 0) {
        const fields: FormField[] = Object.entries(form.form_data).map(([key, value]) => ({
          key,
          label: key.replace(/_/g, ' '),
          type: 'text' as FieldType,
          defaultValue: String(value),
        }));
        setGroups([{ groupName: 'General Questions', fields }]);
      }
    } else if (templateForm) {
      // Creating a new form from a template - copy structure but not data
      setFormData({
        client: 0,
        name: '',
        description: '',
        is_checked: false,
      });
      
      if (templateForm.form_structure && templateForm.form_structure.groups) {
        // Deep copy the structure but clear default values
        const templateGroups: FormGroup[] = templateForm.form_structure.groups.map(group => ({
          groupName: group.groupName,
          fields: group.fields.map(field => ({
            key: field.key,
            label: field.label,
            type: field.type,
            required: field.required,
            defaultValue: '', // Clear default values for template
          })),
        }));
        setGroups(templateGroups);
      } else if (templateForm.form_data && Object.keys(templateForm.form_data).length > 0) {
        // Legacy template - convert to groups without default values
        const fields: FormField[] = Object.keys(templateForm.form_data).map((key) => ({
          key,
          label: key.replace(/_/g, ' '),
          type: 'text' as FieldType,
          defaultValue: '',
        }));
        setGroups([{ groupName: 'General Questions', fields }]);
      }
    } else {
      // Creating a new form from scratch
      setFormData({
        client: 0,
        name: '',
        description: '',
        is_checked: false,
      });
      setGroups([
        {
          groupName: 'Company Questions',
          fields: [
            { key: 'company_name', label: 'Company Name', type: 'text', required: true, defaultValue: '' },
          ],
        },
      ]);
    }
  }, [form, templateForm, open]);

  // Generate unique key from label
  const generateFieldKey = (label: string, groupIndex: number, fieldIndex: number): string => {
    // Convert label to snake_case
    let baseKey = label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
    
    if (!baseKey) {
      baseKey = `field_${groupIndex}_${fieldIndex}`;
    }
    
    // Check for collisions across all groups
    const allKeys = groups.flatMap(g => g.fields.map(f => f.key));
    let finalKey = baseKey;
    let counter = 1;
    
    // Avoid collision with existing keys (except the current field being edited)
    while (allKeys.includes(finalKey) && 
           (groups[groupIndex]?.fields[fieldIndex]?.key !== finalKey)) {
      finalKey = `${baseKey}_${counter}`;
      counter++;
    }
    
    return finalKey;
  };

  const handleAddGroup = () => {
    setGroups([
      ...groups,
      {
        groupName: 'New Group',
        fields: [{ key: '', label: '', type: 'text', defaultValue: '' }],
      },
    ]);
  };

  const handleRemoveGroup = (groupIndex: number) => {
    setGroups(groups.filter((_, i) => i !== groupIndex));
  };

  const handleGroupNameChange = (groupIndex: number, name: string) => {
    const newGroups = [...groups];
    newGroups[groupIndex].groupName = name;
    setGroups(newGroups);
  };

  const handleGroupRepeatableChange = (groupIndex: number, repeatable: boolean) => {
    const newGroups = [...groups];
    newGroups[groupIndex].repeatable = repeatable;
    setGroups(newGroups);
  };

  const handleAddField = (groupIndex: number) => {
    const newGroups = [...groups];
    newGroups[groupIndex].fields.push({
      key: '',
      label: '',
      type: 'text',
      defaultValue: '',
    });
    setGroups(newGroups);
  };

  const handleRemoveField = (groupIndex: number, fieldIndex: number) => {
    const newGroups = [...groups];
    newGroups[groupIndex].fields = newGroups[groupIndex].fields.filter((_, i) => i !== fieldIndex);
    setGroups(newGroups);
  };

  const handleFieldChange = (
    groupIndex: number,
    fieldIndex: number,
    field: keyof FormField,
    value: string | boolean
  ) => {
    const newGroups = [...groups];
    (newGroups[groupIndex].fields[fieldIndex] as any)[field] = value;
    
    // Auto-generate key when label changes
    if (field === 'label' && typeof value === 'string') {
      const newKey = generateFieldKey(value, groupIndex, fieldIndex);
      newGroups[groupIndex].fields[fieldIndex].key = newKey;
    }
    
    setGroups(newGroups);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.client || !formData.name || !formData.description) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    for (const group of groups) {
      if (!group.groupName.trim()) {
        toast({
          title: 'Validation Error',
          description: 'All groups must have a name',
          variant: 'destructive',
        });
        return;
      }
      for (const field of group.fields) {
        if (!field.key.trim() || !field.label.trim()) {
          toast({
            title: 'Validation Error',
            description: 'All fields must have a key and label',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    const form_data: Record<string, any> = {};
    groups.forEach((group) => {
      group.fields.forEach((field) => {
        if (field.key.trim()) {
          form_data[field.key.trim()] = field.defaultValue || '';
        }
      });
    });

    setIsSubmitting(true);
    try {
      if (form) {
        const updateData: AMLFormUpdate = {
          name: formData.name,
          description: formData.description,
          is_checked: formData.is_checked,
          form_data,
          form_structure: { groups },
        };
        await updateAMLForm(form.id, updateData);
        toast({
          title: 'Success',
          description: 'AML form updated successfully',
        });
      } else {
        const createData: AMLFormCreate = {
          client: formData.client,
          name: formData.name,
          description: formData.description,
          is_checked: formData.is_checked,
          form_data,
          form_structure: { groups },
        };
        await createAMLForm(createData);
        toast({
          title: 'Success',
          description: 'AML form created successfully',
        });
      }
      onSaved();
    } catch (error) {
      showErrorToast(error, form ? 'Failed to update AML form' : 'Failed to create AML form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {form?.is_completed 
              ? 'View Completed AML Form' 
              : form 
              ? 'Edit AML Form' 
              : templateForm 
              ? 'Create AML Form from Template' 
              : 'Create AML Form'}
          </DialogTitle>
          <DialogDescription>
            {form?.is_completed
              ? 'Viewing submitted form responses. You can only edit the form name, description, and approval status.'
              : form
              ? 'Update the AML form details and question groups'
              : templateForm
              ? `Creating a new form using "${templateForm.name}" as a template. The structure has been copied - select a client and customize as needed.`
              : 'Create a new AML form with grouped questions for a client to fill out'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="client">Client *</Label>
            <ClientCombobox
              value={formData.client || null}
              onChange={(id) => id && setFormData({ ...formData, client: id })}
              clients={clients}
              placeholder="Select a client"
              disabled={!!form}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Form Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Initial AML Assessment - Acme Corp"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description of the form purpose"
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_checked"
              checked={formData.is_checked}
              onCheckedChange={(checked) => setFormData({ ...formData, is_checked: checked })}
            />
            <Label htmlFor="is_checked">Mark as checked by firm</Label>
          </div>

          {form?.is_completed ? (
            // Read-only view of filled form
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">Submitted Responses</Label>
                {form.completed_at && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed on {new Date(form.completed_at).toLocaleDateString()}
                  </Badge>
                )}
              </div>

              {groups.map((group, groupIndex) => {
                const isRepeatable = group.repeatable || false;
                
                // For repeatable groups, count instances
                let instanceCount = 1;
                if (isRepeatable && form.form_data) {
                  const firstFieldKey = group.fields[0]?.key;
                  if (firstFieldKey) {
                    let count = 0;
                    while (form.form_data.hasOwnProperty(`${groupIndex}_${count}_${firstFieldKey}`)) {
                      count++;
                    }
                    instanceCount = Math.max(1, count);
                  }
                }

                return (
                  <Card key={groupIndex} className="border-2">
                    <CardHeader className="pb-3 bg-muted/50">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{group.groupName}</h3>
                        {isRepeatable && instanceCount > 1 && (
                          <Badge variant="secondary">{instanceCount} entries</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                      {Array.from({ length: instanceCount }).map((_, instanceIndex) => (
                        <div key={instanceIndex} className={instanceCount > 1 ? "p-4 border rounded-lg bg-card space-y-3" : "space-y-3"}>
                          {instanceCount > 1 && (
                            <div className="text-sm font-medium text-muted-foreground border-b pb-2">
                              {group.groupName} #{instanceIndex + 1}
                            </div>
                          )}
                          {group.fields.map((field) => {
                            const fieldKey = isRepeatable 
                              ? `${groupIndex}_${instanceIndex}_${field.key}`
                              : field.key;
                            const filledValue = form.form_data?.[fieldKey];
                            const hasValue = filledValue !== undefined && filledValue !== null && filledValue !== '';
                            
                            return (
                              <div key={field.key} className="space-y-1">
                                <Label className="text-sm font-medium text-foreground">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </Label>
                                <div className={`p-2 rounded-md border ${hasValue ? 'bg-card border-gray-300' : 'bg-muted/50 border-border'}`}>
                                  {field.type === 'boolean' ? (
                                    <div className="flex items-center gap-2">
                                      {filledValue === true || filledValue === 'true' ? (
                                        <>
                                          <CheckCircle className="w-4 h-4 text-green-600" />
                                          <span className="text-green-700 font-medium">Yes</span>
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="w-4 h-4 text-gray-400" />
                                          <span className="text-muted-foreground">No</span>
                                        </>
                                      )}
                                    </div>
                                  ) : field.type === 'text_long' ? (
                                    <div className="whitespace-pre-wrap text-sm">
                                      {hasValue ? filledValue : <span className="text-gray-400 italic">Not provided</span>}
                                    </div>
                                  ) : field.type === 'file' ? (
                                    <div className="text-sm">
                                      {form.documents && form.documents.filter(d => d.field_id === fieldKey).length > 0 ? (
                                        <DocumentUpload
                                          documents={form.documents.filter(d => d.field_id === fieldKey)}
                                          onUpload={async () => {}}
                                          onDownload={async (doc) => {
                                            const blob = await downloadDocumentAuthenticated(form.id, doc.id);
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = doc.original_filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                          }}
                                          onDelete={async () => {}}
                                          readOnly
                                          compact
                                        />
                                      ) : (
                                        <span className="text-gray-400 italic">No files uploaded</span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm">
                                      {hasValue ? String(filledValue) : <span className="text-gray-400 italic">Not provided</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}

              {/* General Documents Section */}
              {form.documents && form.documents.filter(d => !d.field_id).length > 0 && (
                <Card className="border-2">
                  <CardHeader className="pb-3 bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      <h3 className="font-semibold">Supporting Documents</h3>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <DocumentUpload
                      documents={form.documents.filter(d => !d.field_id)}
                      onUpload={async () => {}}
                      onDownload={async (doc) => {
                        const blob = await downloadDocumentAuthenticated(form.id, doc.id);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = doc.original_filename;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }}
                      onDelete={async () => {}}
                      readOnly
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            // Editable form builder
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">Question Groups</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddGroup}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Group
                </Button>
              </div>

              {groups.map((group, groupIndex) => (
              <Card key={groupIndex} className="border-2">
                <CardHeader className="pb-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Group Name (e.g., Company Questions)"
                        value={group.groupName}
                        onChange={(e) => handleGroupNameChange(groupIndex, e.target.value)}
                        className="font-semibold"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGroup(groupIndex)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={group.repeatable || false}
                        onCheckedChange={(checked) => handleGroupRepeatableChange(groupIndex, checked)}
                      />
                      <Label className="text-xs text-muted-foreground">
                        Repeatable Group (users can add multiple instances)
                      </Label>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {group.fields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Question Label (e.g., Company Name)"
                          value={field.label}
                          onChange={(e) =>
                            handleFieldChange(groupIndex, fieldIndex, 'label', e.target.value)
                          }
                          className="font-medium"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            value={field.type}
                            onValueChange={(value) =>
                              handleFieldChange(groupIndex, fieldIndex, 'type', value)
                            }
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="text_long">Text (Long)</SelectItem>
                              <SelectItem value="number">Number</SelectItem>
                              <SelectItem value="boolean">Yes/No</SelectItem>
                              <SelectItem value="file">
                                <span className="flex items-center gap-1">
                                  <Paperclip className="w-3 h-3" />
                                  File Upload
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {field.type === 'boolean' ? (
                            <div className="flex items-center space-x-2 px-3 border rounded-md">
                              <Switch
                                checked={field.defaultValue === true || field.defaultValue === 'true'}
                                onCheckedChange={(checked) =>
                                  handleFieldChange(groupIndex, fieldIndex, 'defaultValue', checked)
                                }
                              />
                              <Label className="text-xs">
                                Default: {field.defaultValue === true || field.defaultValue === 'true' ? 'Yes' : 'No'}
                              </Label>
                            </div>
                          ) : field.type === 'file' ? (
                            <Select
                              value={field.documentType || 'other'}
                              onValueChange={(value) =>
                                handleFieldChange(groupIndex, fieldIndex, 'documentType', value)
                              }
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Document type" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Default Value (optional)"
                              value={String(field.defaultValue || '')}
                              onChange={(e) =>
                                handleFieldChange(groupIndex, fieldIndex, 'defaultValue', e.target.value)
                              }
                              className="text-sm"
                            />
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={field.required || false}
                              onCheckedChange={(checked) =>
                                handleFieldChange(groupIndex, fieldIndex, 'required', checked)
                              }
                            />
                            <Label className="text-xs text-muted-foreground">Required field</Label>
                          </div>
                          {field.key && (
                            <span className="text-xs text-gray-400 font-mono">
                              Key: {field.key}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveField(groupIndex, fieldIndex)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddField(groupIndex)}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Question
                  </Button>
                </CardContent>
              </Card>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : form ? 'Update Form' : 'Create Form'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
